import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import {
  ContextFallbackService,
  ContextSelectorService,
  CurrentUser,
  CurrentUserService,
  PROFILE_REQUIRED_FOR_USER,
} from '@rma/generic/ng/user';
import { firstValueFrom, tap } from 'rxjs';
import { LegacyUserService } from './domain/services/legacy/legacy-user.service';

function initialiseAuth(
  currentUserService: CurrentUserService,
  contextSelectorService: ContextSelectorService,
  contextFallbackService: ContextFallbackService,
  legacyUserService: LegacyUserService,
) {
  return async () => {
    // The fallback subscribes before the user/context streams have emitted, so its subscription is in place when the first
    // bad-pointer resolution arrives. Do not move this after reloadUser - early emissions would slip past it.
    contextFallbackService.initialise();
    await firstValueFrom(currentUserService.reloadUser().pipe(tap((x) => contextSelectorService.initialise(x))));
    await legacyUserService.initialise(null); // Remove when this service is nolonger used
  };
}

export function provideRmaUserInitialisation(): EnvironmentProviders {
  return makeEnvironmentProviders([
    // Dashboard never serves Consumers, so any non-Consumer with an unresolvable selected profile is a bad pointer to recover from.
    { provide: PROFILE_REQUIRED_FOR_USER, useValue: (user: CurrentUser) => user.userType !== 'Consumer' },
    provideAppInitializer(() => {
      const initializerFn = initialiseAuth(
        inject(CurrentUserService),
        inject(ContextSelectorService),
        inject(ContextFallbackService),
        inject(LegacyUserService),
      );
      return initializerFn();
    }),
  ]);
}
